// The Calendar sidebar's modals (CALENDAR-C1 §3, §7): follow a person's calendar, share a
// calendar (a member, a team or the company at busy / read / write; revoke), a calendar's
// settings (name, colour, default visibility), the colour picker, the person's ICS feed (a new
// link shown once with a copy button, revoke), and "Find a time" (the free gaps of a day across
// the shown calendars, from the window already loaded).
import {
  Badge,
  Button,
  CalendarDot,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  type TagChipColor,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, ClockIcon, CopyIcon, PlusIcon, Trash2Icon, UserPlusIcon } from "lucide-react";
import { useId, useState } from "react";
import { type Api, apiUrl } from "../../api/client";
import type { CalendarOccurrence, CalendarView } from "../../api/types";
import { HOVER_STRIP, IconAction } from "../cadence/cadence-parts";
import { orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { CALENDAR_COLORS, clock, dayLabel, freeGaps, pickLength } from "./calendar-model";
import { calendarKeys, feedQuery, sharesQuery } from "./calendar-queries";
import { usePeople } from "./event-dialogs";

const LEVELS = [
  ["busy", "Busy"],
  ["read", "Read"],
  ["write", "Write"],
] as const;
type Level = (typeof LEVELS)[number][0];

function useRefresh(co: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: calendarKeys.root(co) });
}

// Follow ----------------------------------------------------------------------------------------

export function FollowDialog({
  api,
  company,
  calendars,
  myMembershipId,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  calendars: readonly CalendarView[];
  myMembershipId: string | null;
  onClose: () => void;
}) {
  const co = company.id;
  const refresh = useRefresh(co);
  const people = usePeople(api, co);
  const [q, setQ] = useState("");
  const followed = new Set(
    calendars.filter((c) => c.kind === "person" && c.followed).map((c) => c.owner_membership_id),
  );
  const follow = useMutation({
    mutationKey: calendarKeys.selection(co),
    mutationFn: (membership_id: string) =>
      api.client.calendarFollows.create({ params: { co }, body: { membership_id, shown: true } }),
    onSuccess: (c) => {
      void refresh();
      toast.add({ title: "Following", description: c.owner_name ?? c.name, type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The calendar could not be followed."), type: "error" }),
  });
  const shown = people.filter(
    (p) =>
      p.id !== myMembershipId &&
      p.kind === "person" &&
      (!q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())),
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]" data-slot="follow-dialog">
        <DialogHeader>
          <span className="flex items-center gap-1.5 pr-8">
            <DialogTitle>Follow a calendar</DialogTitle>
            <InfoTip label="About following">
              Personal calendars show as busy to others unless you share them.
            </InfoTip>
          </span>
        </DialogHeader>
        <Input
          aria-label="Find people"
          placeholder="Find people"
          value={q}
          autoFocus
          onChange={(e) => setQ(e.target.value)}
        />
        <ul className="m-0 grid max-h-72 list-none gap-0.5 overflow-auto p-0">
          {shown.map((p) => (
            <li key={p.id} className="flex h-9 items-center gap-2 rounded-md px-2 hover:bg-muted">
              <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
              {followed.has(p.id) ? (
                <Badge variant="success">
                  <CheckIcon aria-hidden="true" className="size-3" />
                  Following
                </Badge>
              ) : (
                <Button
                  size="xs"
                  variant="secondary"
                  aria-label={`Follow ${p.name}`}
                  disabled={follow.isPending}
                  onClick={() => follow.mutate(p.id)}
                >
                  <UserPlusIcon data-icon="inline-start" aria-hidden="true" />
                  Follow
                </Button>
              )}
            </li>
          ))}
          {!shown.length ? <li className="px-2 py-1 text-xs text-faint">Nobody matches.</li> : null}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

// Share -----------------------------------------------------------------------------------------

export function ShareDialog({
  api,
  company,
  calendar,
  label,
  onClose,
}: {
  api: Api;
  /** Only its id is read (board settings open it with the company id alone). */
  company: Pick<CompanyAccess, "id">;
  calendar: CalendarView;
  label: string;
  onClose: () => void;
}) {
  const co = company.id;
  const id = useId();
  const refresh = useRefresh(co);
  const shares = useQuery({ ...sharesQuery(api, co, calendar.id), retry: false });
  const people = usePeople(api, co);
  const chart = useQuery({ ...orgChartQuery(api, co), retry: false });
  const teams = (chart.data?.teams ?? []).map((t) => ({ id: t.id, name: t.name }));
  // CAL-WORK-LAYERS: a work calendar is shared to read, with people (DEs too), teams, seats or
  // everyone; the board's viewers always see it.
  const work = calendar.kind === "work";
  const seats = (chart.data?.seats ?? []).map((s) => ({ id: s.id, name: s.title }));
  const [kind, setKind] = useState<"membership" | "team" | "seat" | "company">("membership");
  const [grantee, setGrantee] = useState("");
  const [picked, setLevel] = useState<Level>("read");
  const level: Level = work ? "read" : picked;
  const add = useMutation({
    mutationFn: () =>
      api.client.calendarShares.create({
        params: { co, id: calendar.id },
        body: {
          grantee_kind: kind,
          ...(kind === "company" ? {} : { grantee_id: grantee }),
          level,
        },
      }),
    onSuccess: (s) => {
      void refresh();
      setGrantee("");
      toast.add({ title: "Shared", description: s.grantee_name ?? undefined, type: "success" });
    },
  });
  const revoke = useMutation({
    mutationFn: (share: string) =>
      api.client.calendarShares.delete({ params: { co, id: calendar.id, share } }),
    onSuccess: () => {
      void refresh();
      toast.add({ title: "Share revoked", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The share could not be revoked."), type: "error" }),
  });
  const options =
    kind === "membership"
      ? people
          .filter((p) => p.id !== calendar.owner_membership_id)
          .map((p) => ({
            id: p.id,
            name: p.kind === "digital_employee" ? `${p.name} (DE)` : p.name,
          }))
      : kind === "team"
        ? teams
        : kind === "seat"
          ? seats
          : [];
  const live = (shares.data?.data ?? []).filter((s) => !s.revoked_at);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-slot="share-dialog">
        <DialogHeader>
          <span className="flex items-center gap-1.5 pr-8">
            <DialogTitle>Share {label}</DialogTitle>
            <InfoTip label="About sharing">
              {work
                ? "Everyone who can open the board sees it. Add anyone else here."
                : "Busy shows blocks only; Read shows details; Write lets them add and edit events."}
            </InfoTip>
          </span>
        </DialogHeader>
        {shares.isPending ? (
          <Loading label="Loading shares" rows={2} />
        ) : (
          <ul className="m-0 grid list-none gap-0.5 p-0" aria-label="Shared with">
            {live.map((s) => (
              <li
                key={s.id}
                className="group/row flex h-9 items-center gap-2 rounded-md px-2 hover:bg-muted"
              >
                <span className="min-w-0 flex-1 truncate text-sm">
                  {s.grantee_kind === "company" ? "Everyone" : (s.grantee_name ?? "—")}
                </span>
                {work ? (
                  s.grantee_kind === "seat" ? (
                    <Badge variant="outline">Seat</Badge>
                  ) : s.grantee_kind === "team" ? (
                    <Badge variant="outline">Team</Badge>
                  ) : null
                ) : (
                  <Badge variant="outline">{LEVELS.find(([v]) => v === s.level)?.[1]}</Badge>
                )}
                <span className={HOVER_STRIP}>
                  <IconAction
                    label={`Revoke ${s.grantee_name ?? "share"}`}
                    tip="Revoke"
                    danger
                    onClick={() => revoke.mutate(s.id)}
                  >
                    <Trash2Icon aria-hidden="true" />
                  </IconAction>
                </span>
              </li>
            ))}
            {!live.length ? (
              <li className="px-2 py-1 text-xs text-faint">Not shared with anyone yet.</li>
            ) : null}
          </ul>
        )}
        <form
          className={cn(
            "grid items-end gap-2 max-sm:grid-cols-1",
            work ? "grid-cols-[1fr_1.6fr_auto]" : "grid-cols-[1fr_1.4fr_1fr_auto]",
          )}
          aria-label="Add a share"
          onSubmit={(e) => {
            e.preventDefault();
            if (kind === "company" || grantee) add.mutate();
          }}
        >
          <Field>
            <FieldLabel htmlFor={`${id}-kind`}>With</FieldLabel>
            <NativeSelect
              id={`${id}-kind`}
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as typeof kind);
                setGrantee("");
              }}
              className="w-full"
            >
              <NativeSelectOption value="membership">
                {work ? "Person or DE" : "Person"}
              </NativeSelectOption>
              <NativeSelectOption value="team">Team</NativeSelectOption>
              {work ? <NativeSelectOption value="seat">Seat</NativeSelectOption> : null}
              <NativeSelectOption value="company">Everyone</NativeSelectOption>
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-who`}>Who</FieldLabel>
            <NativeSelect
              id={`${id}-who`}
              value={grantee}
              disabled={kind === "company"}
              onChange={(e) => setGrantee(e.target.value)}
              className="w-full"
            >
              <NativeSelectOption value="">
                {kind === "company" ? "The company" : "Pick…"}
              </NativeSelectOption>
              {options.map((o) => (
                <NativeSelectOption key={o.id} value={o.id}>
                  {o.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {work ? null : (
            <Field>
              <FieldLabel htmlFor={`${id}-lvl`}>Level</FieldLabel>
              <NativeSelect
                id={`${id}-lvl`}
                value={level}
                onChange={(e) => setLevel(e.target.value as Level)}
                className="w-full"
              >
                {LEVELS.map(([v, l]) => (
                  <NativeSelectOption key={v} value={v}>
                    {l}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          )}
          <Button
            type="submit"
            size="sm"
            className="h-9"
            disabled={add.isPending || (kind !== "company" && !grantee)}
          >
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Share
          </Button>
        </form>
        {add.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(add.error, "The calendar could not be shared.")}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// Settings and colour ---------------------------------------------------------------------------

export function ColorSwatches({
  value,
  onPick,
}: {
  value: TagChipColor | null;
  onPick: (c: TagChipColor) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" data-slot="color-swatches">
      {CALENDAR_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={value === c}
          aria-label={`Colour ${c}`}
          onClick={() => onPick(c)}
          className="flex size-7 items-center justify-center rounded-md border border-subtle hover:bg-muted aria-pressed:border-border-strong aria-pressed:bg-muted"
        >
          <CalendarDot color={c} className="size-3.5" />
        </button>
      ))}
    </div>
  );
}

/** The overlay colour of a calendar in the caller's list (its follow's colour). */
export function ColorDialog({
  api,
  company,
  calendar,
  label,
  current,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  calendar: CalendarView;
  label: string;
  current: TagChipColor;
  onClose: () => void;
}) {
  const co = company.id;
  const refresh = useRefresh(co);
  const recolor = useMutation({
    mutationFn: (color: TagChipColor) =>
      api.client.calendarFollows.update({ params: { co, id: calendar.id }, body: { color } }),
    onSuccess: () => {
      void refresh();
      onClose();
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The colour could not be changed."), type: "error" }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[340px]" data-slot="color-dialog">
        <DialogHeader>
          <DialogTitle>Colour of {label}</DialogTitle>
          <DialogDescription>Only in your calendar.</DialogDescription>
        </DialogHeader>
        <ColorSwatches value={current} onPick={(c) => recolor.mutate(c)} />
      </DialogContent>
    </Dialog>
  );
}

export function CalendarSettingsDialog({
  api,
  company,
  calendar,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  calendar: CalendarView;
  onClose: () => void;
}) {
  const co = company.id;
  const id = useId();
  const refresh = useRefresh(co);
  const [name, setName] = useState(calendar.name);
  const [color, setColor] = useState<TagChipColor | null>(
    (CALENDAR_COLORS as readonly string[]).includes(calendar.color ?? "")
      ? (calendar.color as TagChipColor)
      : null,
  );
  const [visibility, setVisibility] = useState(calendar.visibility);
  const save = useMutation({
    mutationFn: () =>
      api.client.calendars.update({
        params: { co, id: calendar.id },
        body: { name: name.trim(), color, visibility },
      }),
    onSuccess: () => {
      void refresh();
      toast.add({ title: "Calendar saved", type: "success" });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Calendar settings"
      submitLabel="Save"
      pending={save.isPending}
      canSubmit={Boolean(name.trim())}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="calendar-settings"
      error={
        save.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(save.error, "The calendar could not be saved.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
        <Input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field>
        <span className="flex items-center gap-1">
          <FieldLabel htmlFor={`${id}-vis`}>Others see</FieldLabel>
          <InfoTip label="About default visibility">
            What people who are not on the calendar see of its events. An event can override it.
          </InfoTip>
        </span>
        <NativeSelect
          id={`${id}-vis`}
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as typeof visibility)}
          className="w-full"
        >
          <NativeSelectOption value="details">Details</NativeSelectOption>
          <NativeSelectOption value="busy">Busy</NativeSelectOption>
          <NativeSelectOption value="hidden">Nothing</NativeSelectOption>
        </NativeSelect>
      </Field>
      <div className="grid gap-2">
        <span className="text-sm font-medium text-foreground">Colour</span>
        <ColorSwatches value={color} onPick={setColor} />
      </div>
    </QuickAddDialog>
  );
}

// ICS feed --------------------------------------------------------------------------------------

export function FeedDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const status = useQuery({ ...feedQuery(api, co), retry: false });
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: calendarKeys.feed(co) });
  const make = useMutation({
    mutationFn: () => api.client.calendarFeed.create({ params: { co } }),
    onSuccess: (r) => {
      void refresh();
      const base = api.baseUrl ? apiUrl(api.baseUrl, r.path) : r.path;
      setLink(new URL(base, window.location.origin).href);
      setCopied(false);
    },
  });
  const revoke = useMutation({
    mutationFn: () => api.client.calendarFeed.delete({ params: { co } }),
    onSuccess: () => {
      void refresh();
      setLink(null);
      toast.add({ title: "Feed link revoked", type: "success" });
    },
  });
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const active = status.data?.active ?? false;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-slot="feed-dialog">
        <DialogHeader>
          <span className="flex items-center gap-1.5 pr-8">
            <DialogTitle>Subscribe (ICS)</DialogTitle>
            <InfoTip label="About the ICS feed">
              A private, read-only link to what you see here, for Apple, Google or Outlook
              calendars. Anyone with the link can read it; make a new one to cut off the old.
            </InfoTip>
          </span>
        </DialogHeader>
        {status.isPending ? (
          <Loading label="Loading the feed" rows={1} />
        ) : (
          <p className="text-sm text-secondary" data-slot="feed-status">
            {active ? (
              <>
                <Badge variant="success">Active</Badge>
                {status.data?.last_used_at ? (
                  <span className="ml-2 text-xs text-muted-foreground">
                    Last read {new Date(status.data.last_used_at).toLocaleDateString("en-GB")}
                  </span>
                ) : null}
              </>
            ) : (
              <Badge variant="neutral">No link</Badge>
            )}
          </p>
        )}
        {link ? (
          <div className="grid gap-1.5">
            <span className="text-xs text-muted-foreground">Shown once. Copy it now.</span>
            <div className="flex gap-2">
              <Input
                readOnly
                value={link}
                aria-label="Feed link"
                onFocus={(e) => e.target.select()}
              />
              <Button variant="secondary" onClick={() => void copy()}>
                {copied ? (
                  <CheckIcon data-icon="inline-start" aria-hidden="true" />
                ) : (
                  <CopyIcon data-icon="inline-start" aria-hidden="true" />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        ) : null}
        {make.isError || revoke.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(make.error ?? revoke.error, "The feed could not be changed.")}
          </p>
        ) : null}
        <DialogFooter>
          {active ? (
            <Button variant="ghost" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
              Revoke
            </Button>
          ) : null}
          <Button disabled={make.isPending} onClick={() => make.mutate()}>
            {active ? "New link" : "Make a link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Find a time -----------------------------------------------------------------------------------

export function FindTimeDialog({
  days,
  initialDay,
  occurrences,
  truncated = false,
  status,
  onPick,
  onClose,
}: {
  /** The days loaded (the view's window): the gaps come from these occurrences. */
  days: readonly string[];
  initialDay: string;
  occurrences: readonly CalendarOccurrence[];
  /** The window was cut at the API's cap: no suggestions from an incomplete list. */
  truncated?: boolean;
  /** The window's read: suggestions only once it has loaded. */
  status: "pending" | "error" | "success";
  /** A gap chosen: its start and a length that ends inside it. */
  onPick: (day: string, hour: number, minute: number, length: number) => void;
  onClose: () => void;
}) {
  const id = useId();
  const [day, setDay] = useState(days.includes(initialDay) ? initialDay : (days[0] ?? initialDay));
  const [minutes, setMinutes] = useState(30);
  const gaps = freeGaps(day, occurrences, { minMinutes: minutes });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px]" data-slot="find-time">
        <DialogHeader>
          <span className="flex items-center gap-1.5 pr-8">
            <DialogTitle>Find a time</DialogTitle>
            <InfoTip label="About find a time">
              Free gaps between 08:00 and 18:00 across the calendars shown, busy blocks included.
            </InfoTip>
          </span>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor={`${id}-day`}>Day</FieldLabel>
            <NativeSelect
              id={`${id}-day`}
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="w-full"
            >
              {days.map((d) => (
                <NativeSelectOption key={d} value={d}>
                  {dayLabel(d, "long")}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-len`}>At least</FieldLabel>
            <NativeSelect
              id={`${id}-len`}
              value={String(minutes)}
              onChange={(e) => setMinutes(Number(e.target.value))}
              className="w-full"
            >
              {[15, 30, 60, 90, 120].map((m) => (
                <NativeSelectOption key={m} value={String(m)}>
                  {m < 60 ? `${m} min` : `${m / 60} h`}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        </div>
        {status === "pending" ? (
          <Loading label="Loading events" rows={1} />
        ) : status === "error" ? (
          <EmptyState
            variant="compact"
            icon={ClockIcon}
            title="Couldn't load events"
            data-slot="find-time-error"
          />
        ) : truncated ? (
          // The window came back incomplete: a missing busy block would read as free time.
          <EmptyState
            variant="compact"
            icon={ClockIcon}
            title="Too many events to suggest a time"
            data-slot="find-time-truncated"
          />
        ) : gaps.length ? (
          <ul className="m-0 grid list-none gap-1 p-0" aria-label="Free times">
            {gaps.map((g) => (
              <li
                key={g.start.toISOString()}
                className="group/row flex h-9 items-center gap-2 rounded-md border border-subtle px-2"
              >
                <ClockIcon aria-hidden="true" className="size-3.5 text-faint" />
                <span className="flex-1 text-sm tabular-nums">
                  {clock(g.start)} – {clock(g.end)}
                </span>
                <Button
                  size="xs"
                  variant="secondary"
                  aria-label={`New event at ${clock(g.start)}`}
                  onClick={() =>
                    onPick(day, g.start.getHours(), g.start.getMinutes(), pickLength(g, minutes))
                  }
                >
                  <PlusIcon data-icon="inline-start" aria-hidden="true" />
                  Add
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState variant="compact" icon={ClockIcon} title="No free time that long" />
        )}
      </DialogContent>
    </Dialog>
  );
}
